import axios from 'axios';
import { API_BASE_URL, TOKEN_KEY, SITE_KEY } from '../utils/constants';

// ── Types ──────────────────────────────────────────────────────────────
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  organizationName: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'viewer';
  avatarUrl?: string;
}

export interface Site {
  id: string;
  name: string;
  domain: string;
  trackingId: string;
  settings: SiteSettings;
  createdAt: string;
}

export interface SiteSettings {
  samplingRate: number;
  recordConsole: boolean;
  recordNetwork: boolean;
  maskInputs: boolean;
  maskText: boolean;
  retentionDays: number;
}

export interface Session {
  id: string;
  visitorId: string;
  siteId?: string;
  startedAt: string;
  endedAt?: string;
  duration?: number;
  durationMs?: number;
  pageCount?: number;
  pagesVisited?: number;
  entryPage?: string;
  entryUrl?: string;
  exitPage?: string;
  exitUrl?: string;
  device?: string;
  deviceType?: string;
  browser?: string;
  browserVersion?: string;
  os?: string;
  country?: string | null;
  city?: string;
  screenWidth?: number;
  screenHeight?: number;
  hasRageClicks: boolean;
  hasErrors: boolean;
  hasRecording?: boolean;
  eventsCount?: number;
  userId?: string | null;
  userTraits?: Record<string, unknown> | null;
}

export interface SessionEvent {
  id: string;
  sessionId: string;
  type: string;
  timestamp: string;
  data: Record<string, unknown>;
}

export interface DashboardStats {
  totalSessions: number;
  totalPageViews?: number;
  uniqueVisitors: number;
  avgSessionDuration?: number;
  avgDuration?: number;
  bounceRate: number;
  rageClickSessions?: number;
  errorSessions?: number;
  sessionsChange?: number;
  visitorsChange?: number;
  durationChange?: number;
  bounceRateChange?: number;
  sessionsOverTime: Array<{ date: string; count?: number; sessions?: number; visitors?: number }>;
  deviceBreakdown: Array<{ deviceType?: string; device?: string; count: number; percentage?: number }>;
  topPages: Array<{ urlPath?: string; url?: string; views: number; avgTime?: number }>;
  topReferrers: Array<{ referrer: string; sessions?: number; count?: number }>;
  topCountries?: Array<{ country: string | null; count: number }>;
  recentRageClicks?: Array<{ selector: string; page: string; count: number; lastSeen: string }>;
  recentErrors?: Array<{ message: string; count: number; lastSeen: string }>;
}

export interface HeatmapData {
  url?: string;
  urlPath?: string;
  type: 'click' | 'scroll' | 'move' | 'rage';
  points: Array<{ x: number; y: number; count?: number; value?: number }>;
  positions?: Array<{ x: number; y: number }>;
  distribution?: Array<{ depth: number; count: number; percentage: number }>;
  totalSessions?: number;
  totalInteractions?: number;
  pageWidth?: number;
  pageHeight?: number;
}

export interface Funnel {
  id: string;
  name: string;
  siteId: string;
  steps: FunnelStep[];
  createdAt: string;
}

export interface FunnelStep {
  name: string;
  type: 'pageview' | 'event';
  value: string;
}

export interface FunnelResult {
  id: string;
  name: string;
  steps: Array<{
    name: string;
    visitors: number;
    dropoff: number;
    dropoffRate: number;
    conversionRate: number;
  }>;
  totalConversion: number;
}

export interface FormAnalytics {
  formId: string;
  formName: string;
  url: string;
  fields: FormField[];
  totalStarts: number;
  totalSubmissions: number;
  completionRate: number;
  avgCompletionTime: number;
}

export interface FormField {
  name: string;
  label: string;
  type: string;
  interactions: number;
  dropoffs: number;
  dropoffRate: number;
  avgTimeSpent: number;
  refills: number;
  leftBlank: number;
}

export interface ErrorRecord {
  id?: string;
  errorGroupHash?: string;
  message: string;
  type?: string | null;
  errorType?: string | null;
  stack?: string | null;
  latestStack?: string | null;
  url?: string;
  urlPath?: string;
  count?: number;
  occurrences?: number;
  sessionsAffected?: number;
  affectedSessions?: number;
  firstSeen: string;
  lastSeen: string;
  status?: 'open' | 'resolved' | 'ignored';
}

export interface Survey {
  id: string;
  siteId: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'completed';
  trigger: SurveyTrigger;
  questions: SurveyQuestion[];
  responseCount: number;
  createdAt: string;
}

export interface SurveyTrigger {
  type: 'pageview' | 'exit_intent' | 'time_on_page' | 'scroll_depth';
  value: string;
  url?: string;
}

export interface SurveyQuestion {
  id: string;
  type: 'rating' | 'text' | 'multiple_choice' | 'nps';
  text: string;
  options?: string[];
  required: boolean;
}

export interface SurveyResponse {
  id: string;
  surveyId: string;
  sessionId: string;
  answers: Array<{ questionId: string; value: string | number }>;
  submittedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'viewer';
  joinedAt: string;
}

// ── Axios Instance ─────────────────────────────────────────────────────
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const siteId = localStorage.getItem(SITE_KEY);
  if (siteId) {
    config.headers['X-Site-Id'] = siteId;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

// ── Auth ────────────────────────────────────────────────────────────────
export async function login(data: LoginRequest): Promise<AuthResponse> {
  const res = await api.post<AuthResponse>('/auth/login', data);
  return res.data;
}

export async function register(data: RegisterRequest): Promise<AuthResponse> {
  const res = await api.post<AuthResponse>('/auth/register', data);
  return res.data;
}

// ── Sessions ────────────────────────────────────────────────────────────
export async function getSessions(params: {
  page?: number;
  pageSize?: number;
  dateRange?: string;
  device?: string;
  hasErrors?: boolean;
  hasRageClicks?: boolean;
  url?: string;
  customerId?: string;
  emailHash?: string;
  phoneHash?: string;
}): Promise<PaginatedResponse<Session>> {
  const res = await api.get('/sessions', { params });
  const raw = res.data as any;
  // Server returns { sessions: [...], pagination: {...} } — normalize to PaginatedResponse
  if (raw.sessions) {
    return {
      data: raw.sessions,
      total: raw.pagination?.total ?? raw.total ?? 0,
      page: raw.pagination?.page ?? raw.page ?? 1,
      pageSize: raw.pagination?.limit ?? raw.pagination?.pageSize ?? 25,
      totalPages: raw.pagination?.totalPages ?? 1,
    };
  }
  return raw;
}

// ── Checkout Insights ───────────────────────────────────────────────────
export interface CheckoutInsights {
  window: { from: string; to: string; dateRange: string };
  totalSessions: number;
  funnel: Array<{ key: string; label: string; count: number; percentage: number; dropoff?: number }>;
  variants: Array<{ variant: string; views: number; completions: number; cvr: number }>;
  devices: Array<{ device: string; views: number; completions: number; cvr: number }>;
  exitClusters: Array<{ step: string; count: number }>;
  stepTimings: Array<{ step: number; label: string; avgMs: number | null; completions: number }>;
  friction: {
    unserviceablePostcode: number;
    promoFailed: number;
    paymentFailed: number;
    paymentSubmitted: number;
    tabSwitchReturns: number;
    topUnserviceablePostcodes: Array<{ value: string; count: number }>;
    topDeclineCodes: Array<{ value: string; count: number }>;
    topPromoFailReasons: Array<{ value: string; count: number }>;
  };
  recentAbandoned: Array<{
    sessionId: string;
    startedAt: string;
    lastStep: number | null;
    variant: string | null;
    device: string | null;
    exitTrigger: string | null;
    returnedAfterExit: boolean;
  }>;
}

export async function getCheckoutInsights(params: {
  siteId: string;
  dateRange?: string;
}): Promise<CheckoutInsights> {
  const res = await api.get<CheckoutInsights>('/insights/checkout', { params });
  return res.data;
}

export async function getSessionEvents(sessionId: string): Promise<SessionEvent[]> {
  const res = await api.get(`/sessions/${sessionId}/events`);
  const raw = res.data as any;
  // Server returns { events: [...] } — normalize to array
  if (Array.isArray(raw)) return raw;
  if (raw?.events && Array.isArray(raw.events)) return raw.events;
  return [];
}

export interface ConsoleLogEntry {
  id: number;
  sessionId: string;
  level: 'log' | 'info' | 'warn' | 'error' | 'debug';
  message: string;
  stack?: string;
  url?: string;
  timestamp: number;
  createdAt: string;
}

export async function getSessionConsoleLogs(sessionId: string): Promise<ConsoleLogEntry[]> {
  const res = await api.get(`/sessions/${sessionId}/console`);
  return Array.isArray(res.data) ? res.data : [];
}

// ── Dashboard ───────────────────────────────────────────────────────────
export async function getDashboardStats(params: {
  dateRange?: string;
  device?: string;
}): Promise<DashboardStats> {
  const res = await api.get<DashboardStats>('/dashboard/stats', { params });
  return res.data;
}

// ── Heatmaps ────────────────────────────────────────────────────────────
export async function getHeatmapData(params: {
  url: string;
  type: string;
  dateRange?: string;
  device?: string;
}): Promise<HeatmapData> {
  // Backend expects 'urlPath' not 'url'
  const { url, ...rest } = params;
  const res = await api.get<HeatmapData>('/heatmaps', {
    params: { urlPath: url, ...rest },
  });
  return res.data;
}

// ── Landing Page Intelligence ─────────────────────────────────────────
export interface LandingMetricRow {
  key: string;
  label: string;
  sessions: number;
  validSessions: number;
  engagedSessions: number;
  shortExits: number;
  productClicks: number;
  addToCarts: number;
  checkouts: number;
  conversions: number;
  revenue: number;
  medianActiveSeconds: number;
  engagedRate: number;
  shortExitRate: number;
  productClickRate: number;
  addToCartRate: number;
  checkoutRate: number;
  conversionRate: number;
  revenuePerSession: number;
  recordingCoverage: number;
  sampleStatus: 'low' | 'directional' | 'reliable';
}

export interface LandingPageReport {
  generatedAt: string;
  summary: LandingMetricRow;
  pages: LandingMetricRow[];
  sources: LandingMetricRow[];
  campaigns: LandingMetricRow[];
  ads: LandingMetricRow[];
  placements: LandingMetricRow[];
  devices: LandingMetricRow[];
  experiments: LandingMetricRow[];
  assets: Array<LandingMetricRow & { assetUrl: string | null; assetType: string; section: string | null; clicks: number; interpretation: 'association_only' }>;
  replayQueues: Array<{ name: string; description: string; sessions: Array<{ id: string; path: string; source: string; campaign: string; device: string; activeSeconds: number; reason: string }> }>;
  findings: Array<{ severity: 'critical' | 'warning' | 'opportunity' | 'positive'; title: string; detail: string }>;
  options: { paths: string[]; sources: string[]; campaigns: string[]; devices: string[] };
  dataQuality: { loadedSessions: number; validSessions: number; activeTimeCoverage: number; recordingCoverage: number; campaignIdCoverage: number; adIdCoverage: number; assetCoverage: number; notes: string[] };
}

export async function getLandingPageReport(params: {
  dateRange?: string;
  path?: string;
  source?: string;
  campaign?: string;
  adId?: string;
  placement?: string;
  device?: string;
}): Promise<LandingPageReport> {
  const res = await api.get<LandingPageReport>('/landing-pages', { params });
  return res.data;
}

// ── Funnels ─────────────────────────────────────────────────────────────
export async function getFunnels(): Promise<Funnel[]> {
  const res = await api.get('/funnels');
  const raw = res.data as any;
  if (Array.isArray(raw)) return raw;
  if (raw?.funnels && Array.isArray(raw.funnels)) return raw.funnels;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

export async function createFunnel(data: {
  name: string;
  steps: FunnelStep[];
}): Promise<Funnel> {
  const res = await api.post<Funnel>('/funnels', data);
  return res.data;
}

export async function getFunnelById(id: string): Promise<FunnelResult> {
  const res = await api.get<FunnelResult>(`/funnels/${id}`);
  return res.data;
}

// ── Forms ───────────────────────────────────────────────────────────────
export async function getFormAnalytics(params?: {
  dateRange?: string;
}): Promise<FormAnalytics[]> {
  const res = await api.get('/forms', { params });
  const raw = res.data as any;
  let forms: any[];
  if (Array.isArray(raw)) forms = raw;
  else if (raw?.forms && Array.isArray(raw.forms)) forms = raw.forms;
  else if (raw?.data && Array.isArray(raw.data)) forms = raw.data;
  else forms = [];

  // Normalize API response fields to match FormAnalytics interface
  return forms.map((f: any) => ({
    formId: f.formId || f.formSelector || f.id || '',
    formName: f.formName || f.formSelector || f.urlPath || 'Unknown Form',
    url: f.url || f.urlPath || '',
    fields: (f.fields || []).map((field: any) => ({
      name: field.name || field.fieldName || '',
      label: field.label || field.name || field.fieldName || '',
      type: field.type || 'text',
      interactions: field.interactions ?? 0,
      dropoffs: field.dropoffs ?? 0,
      dropoffRate: field.dropoffRate ?? 0,
      avgTimeSpent: field.avgTimeSpent ?? field.avgTimeMs ?? 0,
      refills: field.refills ?? field.totalRefills ?? 0,
      leftBlank: field.leftBlank ?? 0,
    })),
    totalStarts: f.totalStarts ?? f.totalInteractions ?? 0,
    totalSubmissions: f.totalSubmissions ?? f.submissions ?? 0,
    completionRate:
      f.completionRate ??
      (f.totalInteractions > 0
        ? (f.submissions / f.totalInteractions) * 100
        : 0),
    avgCompletionTime: f.avgCompletionTime ?? 0,
  }));
}

// ── Errors ──────────────────────────────────────────────────────────────
export async function getErrors(params?: {
  page?: number;
  pageSize?: number;
  status?: string;
  dateRange?: string;
}): Promise<PaginatedResponse<ErrorRecord>> {
  const res = await api.get('/errors', { params });
  const raw = res.data as any;
  // Server returns { errors: [...], pagination?: {...} } — normalize to PaginatedResponse
  if (raw?.errors && Array.isArray(raw.errors)) {
    const normalized = raw.errors.map((e: any) => ({
      ...e,
      id: e.id || e.errorGroupHash || '',
      type: e.type || e.errorType || null,
      stack: e.stack || e.latestStack || null,
      url: e.url || e.urlPath || '',
      count: e.count ?? e.occurrences ?? 0,
      sessionsAffected: e.sessionsAffected ?? e.affectedSessions ?? 0,
      status: e.status || 'open',
    }));
    return {
      data: normalized,
      total: raw.pagination?.total ?? raw.total ?? normalized.length,
      page: raw.pagination?.page ?? raw.page ?? 1,
      pageSize: raw.pagination?.limit ?? raw.pagination?.pageSize ?? params?.pageSize ?? 20,
      totalPages: raw.pagination?.totalPages ?? 1,
    };
  }
  // Already in PaginatedResponse format
  if (raw?.data && Array.isArray(raw.data)) return raw;
  return { data: [], total: 0, page: 1, pageSize: params?.pageSize ?? 20, totalPages: 1 };
}

export async function getErrorSessions(errorId: string): Promise<Session[]> {
  const res = await api.get(`/errors/${errorId}/sessions`);
  const raw = res.data as any;
  if (Array.isArray(raw)) return raw;
  if (raw?.sessions && Array.isArray(raw.sessions)) return raw.sessions;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

// ── Surveys ─────────────────────────────────────────────────────────────
export async function getSurveys(): Promise<Survey[]> {
  const res = await api.get('/surveys');
  const raw = res.data as any;
  if (Array.isArray(raw)) return raw;
  if (raw?.surveys && Array.isArray(raw.surveys)) return raw.surveys;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

export async function createSurvey(data: Omit<Survey, 'id' | 'siteId' | 'responseCount' | 'createdAt'>): Promise<Survey> {
  const res = await api.post<Survey>('/surveys', data);
  return res.data;
}

export async function updateSurvey(id: string, data: Partial<Survey>): Promise<Survey> {
  const res = await api.put<Survey>(`/surveys/${id}`, data);
  return res.data;
}

export async function deleteSurvey(id: string): Promise<void> {
  await api.delete(`/surveys/${id}`);
}

export async function getSurveyResponses(surveyId: string): Promise<SurveyResponse[]> {
  const res = await api.get(`/surveys/${surveyId}/responses`);
  const raw = res.data as any;
  if (Array.isArray(raw)) return raw;
  if (raw?.responses && Array.isArray(raw.responses)) return raw.responses;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

// ── Sites ───────────────────────────────────────────────────────────────
export async function getSites(): Promise<Site[]> {
  const res = await api.get('/sites');
  const raw = res.data as any;
  // Server returns { sites: [...] } — normalize to array
  if (Array.isArray(raw)) return raw;
  if (raw?.sites && Array.isArray(raw.sites)) return raw.sites;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

export async function createSite(data: { name: string; domain: string }): Promise<Site> {
  const res = await api.post<Site>('/sites', data);
  return res.data;
}

export async function updateSite(id: string, data: Partial<SiteSettings>): Promise<Site> {
  const res = await api.put<Site>(`/sites/${id}`, data);
  return res.data;
}

// ── Plans & Billing ────────────────────────────────────────────────────
export interface Plan {
  id: string;
  name: string;
  slug: string;
  monthlySessionLimit: number;
  monthlyPriceCents: number;
  annualPriceCents?: number;
  features: Record<string, boolean>;
  maxSites: number;
  maxTeamMembers: number;
  retentionDays: number;
  isPublic: boolean;
  sortOrder: number;
}

export interface Subscription {
  id: string;
  organizationId: string;
  planId: string;
  plan?: Plan;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'paused';
  billingCycle: 'monthly' | 'annual';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt?: string;
  canceledAt?: string;
}

export interface UsageData {
  sessionCount: number;
  sessionLimit: number;
  percentUsed: number;
  limitReached: boolean;
  periodStart: string;
  periodEnd: string;
}

export interface Invoice {
  id: string;
  amountCents: number;
  currency: string;
  status: 'draft' | 'paid' | 'failed' | 'refunded' | 'void';
  periodStart?: string;
  periodEnd?: string;
  paidAt?: string;
  createdAt: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
}

export async function getPlans(): Promise<Plan[]> {
  const res = await api.get('/plans');
  return res.data?.plans || res.data || [];
}

export async function getSubscription(): Promise<Subscription> {
  const res = await api.get('/billing/subscription');
  return res.data;
}

export async function getUsage(): Promise<UsageData> {
  const res = await api.get('/billing/usage');
  return res.data;
}

export async function getInvoices(): Promise<Invoice[]> {
  const res = await api.get('/billing/invoices');
  return res.data?.invoices || res.data || [];
}

export async function createCheckoutSession(
  planId: string,
  successUrl: string,
  cancelUrl: string,
): Promise<{ checkoutUrl: string }> {
  const res = await api.post('/billing/checkout', { planId, successUrl, cancelUrl });
  return res.data;
}

export async function changePlan(planId: string): Promise<Subscription> {
  const res = await api.post('/billing/change-plan', { planId });
  return res.data;
}

export async function cancelSubscription(): Promise<{ success: boolean; activeUntil: string }> {
  const res = await api.post('/billing/cancel');
  return res.data;
}

// ── Team (legacy invite-based) ─────────────────────────────────────────
export async function getTeamMembers(): Promise<TeamMember[]> {
  const res = await api.get('/team');
  return res.data?.members || res.data || [];
}

export async function inviteTeamMember(email: string, role: string): Promise<TeamMember> {
  const res = await api.post('/team/invite', { email, role });
  return res.data;
}

export async function updateTeamMemberRole(memberId: string, role: string): Promise<void> {
  await api.put(`/team/${memberId}/role`, { role });
}

export async function removeTeamMember(memberId: string): Promise<void> {
  await api.delete(`/team/${memberId}`);
}

// ── User Management (admin direct creation) ────────────────────────────
export async function createUserDirect(data: {
  email: string;
  name: string;
  password: string;
  role: 'admin' | 'viewer';
}): Promise<User> {
  const res = await api.post('/users', data);
  return res.data.user;
}

export async function updateUser(
  id: string,
  data: { role?: string; name?: string; password?: string },
): Promise<User> {
  const res = await api.put(`/users/${id}`, data);
  return res.data.user;
}

export async function deleteUserById(id: string): Promise<void> {
  await api.delete(`/users/${id}`);
}

// ── Admin API ───────────────────────────────────────────────────────
export async function getAdminCustomers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}): Promise<{ customers: any[]; pagination: any }> {
  const res = await api.get('/admin/customers', { params });
  return res.data;
}

export async function getAdminCustomerDetail(orgId: string): Promise<any> {
  const res = await api.get(`/admin/customers/${orgId}`);
  return res.data;
}

export async function updateAdminCustomer(
  orgId: string,
  data: { name?: string; slug?: string },
): Promise<any> {
  const res = await api.put(`/admin/customers/${orgId}`, data);
  return res.data;
}

export async function deactivateAdminCustomer(orgId: string): Promise<any> {
  const res = await api.delete(`/admin/customers/${orgId}`);
  return res.data;
}

export async function getAdminPlans(): Promise<Plan[]> {
  const res = await api.get('/admin/plans');
  return res.data?.plans || res.data || [];
}

export async function createAdminPlan(data: Partial<Plan>): Promise<Plan> {
  const res = await api.post('/admin/plans', data);
  return res.data;
}

export async function updateAdminPlan(id: string, data: Partial<Plan>): Promise<Plan> {
  const res = await api.put(`/admin/plans/${id}`, data);
  return res.data;
}

export async function archiveAdminPlan(id: string): Promise<any> {
  const res = await api.delete(`/admin/plans/${id}`);
  return res.data;
}

export async function overridePlan(orgId: string, planId: string): Promise<any> {
  const res = await api.post(`/admin/billing/customers/${orgId}/override-plan`, { planId });
  return res.data;
}

export async function createCredit(
  orgId: string,
  amountCents: number,
  reason: string,
): Promise<any> {
  const res = await api.post(`/admin/billing/customers/${orgId}/credit`, {
    amountCents,
    reason,
  });
  return res.data;
}

export async function refundInvoice(orgId: string, invoiceId: string): Promise<any> {
  const res = await api.post(`/admin/billing/customers/${orgId}/refund`, { invoiceId });
  return res.data;
}

export async function getAdminRevenue(): Promise<any> {
  const res = await api.get('/admin/billing/revenue');
  return res.data;
}

export async function impersonateUser(
  userId: string,
): Promise<{ token: string; user: any; organization: any }> {
  const res = await api.post(`/admin/impersonate/${userId}`);
  return res.data;
}

// ── AI Insights ─────────────────────────────────────────────────────
export type InsightType = 'page_performance' | 'traffic_source' | 'ux_issue' | 'form_optimization' | 'conversion_flow' | 'error_impact' | 'device_browser' | 'landing_page_intelligence' | 'session_deep' | 'pattern_detection' | 'weekly_digest';
export type InsightCategory = 'critical' | 'warning' | 'opportunity' | 'positive';
export type InsightStatus = 'active' | 'dismissed' | 'resolved' | 'expired';

export interface Insight {
  id: string;
  siteId: string;
  type: InsightType;
  category: InsightCategory;
  title: string;
  description: string;
  evidence?: Record<string, any>;
  affectedPages?: string[];
  affectedElements?: string[];
  impactScore: number;
  status: InsightStatus;
  createdAt: string;
  expiresAt?: string;
  dismissedAt?: string;
}

export interface AnalysisRun {
  id: string;
  type: string;
  status: 'running' | 'completed' | 'failed';
  sessionsAnalyzed?: number;
  insightsGenerated?: number;
  tokensUsed?: number;
  durationMs?: number;
  startedAt: string;
  completedAt?: string;
}

export async function getInsights(params?: {
  type?: string;
  category?: string;
  status?: string;
  dateRange?: string;
  page?: number;
  limit?: number;
}): Promise<{ insights: Insight[]; pagination: any }> {
  const res = await api.get('/insights', { params: { status: 'active', ...params } });
  const raw = res.data as any;
  return {
    insights: raw.insights || raw.data || [],
    pagination: raw.pagination || { page: 1, total: 0, totalPages: 0 },
  };
}

export async function getInsight(id: string): Promise<Insight> {
  const res = await api.get(`/insights/${id}`);
  return res.data;
}

export async function dismissInsight(id: string): Promise<void> {
  await api.post(`/insights/${id}/dismiss`);
}

export async function resolveInsight(id: string): Promise<void> {
  await api.post(`/insights/${id}/resolve`);
}

export interface SessionAnalysis {
  sessionId: string;
  journey: {
    deviceType: string;
    browser: string;
    browserVersion: string;
    os: string;
    referrer: string | null;
    entryUrl: string;
    exitUrl: string;
    durationMs: number;
    pagesVisited: number;
    signals: {
      hasRageClicks: boolean;
      rageClickCount: number;
      hasErrors: boolean;
      errorCount: number;
      hasFormAbandonment: boolean;
      totalClickCount: number;
      averageScrollDepth: number;
      bounced: boolean;
      reachedCheckout: boolean;
      backtrackCount: number;
      deadClickCount: number;
      rapidPageChanges: number;
    };
    pageCount: number;
    clickCount: number;
    errorCount: number;
  };
  analysis: {
    intent?: string;
    summary?: string;
    journey_analysis?: string;
    friction_points?: string[];
    positive_signals?: string[];
    exit_reason?: string;
    device_impact?: string;
    recommendations?: string[];
    engagement_score?: number;
    conversion_likelihood?: string;
    segment?: string;
  };
  tokensUsed: number;
}

export async function analyzeSession(sessionId: string): Promise<SessionAnalysis & { narrative?: string }> {
  const res = await api.post(`/insights/session/${sessionId}`);
  return res.data;
}

export async function querySession(
  sessionId: string,
  question: string,
  narrative?: string,
): Promise<{ answer: string; tokensUsed: number }> {
  const res = await api.post(`/insights/session/${sessionId}/query`, { question, narrative });
  return res.data;
}

export async function triggerAnalysis(type?: string): Promise<{ analysisRun: AnalysisRun }> {
  const res = await api.post('/insights/analyze', type ? { type } : {});
  return res.data;
}

export async function getInsightsSummary(): Promise<Insight[]> {
  const res = await api.get('/insights/summary');
  const raw = res.data as any;
  return raw.insights || raw.data || [];
}

export async function getAnalysisRuns(params?: { page?: number; limit?: number }): Promise<{ runs: AnalysisRun[]; pagination: any }> {
  const res = await api.get('/insights/runs', { params });
  const raw = res.data as any;
  return { runs: raw.runs || raw.data || [], pagination: raw.pagination || {} };
}

export default api;
